<template>
    <div class="content product-install-page">
        <div class="container">
            <div class="page-title">
                <h2 class="tit">제품 설치</h2>
            </div>

            <!-- 검색 바 -->
            <div class="search-section">
                <div class="search-bar">
                    <input 
                        type="text" 
                        v-model="searchText" 
                        @keyup.enter="doSearch"
                        placeholder="검색..." 
                        class="search-input"
                    />
                    <button @click="doSearch" class="search-btn">검색</button>
                </div>
            </div>

            <!-- 제품 리스트 -->
            <div v-if="loading" class="loading">불러오는 중...</div>
            <div v-else-if="productList.length === 0" class="empty">제품이 없습니다.</div>
            <div v-else class="product-grid">
                <div 
                    v-for="group in groupedProducts" 
                    :key="group.productName"
                    class="product-item-wrapper"
                >
                    <!-- 제품 아이콘 (카드 밖으로 분리) -->
                    <div class="product-icon">
                        <img 
                            v-if="group.representative && group.representative.iconUrl" 
                            :src="group.representative.iconUrl" 
                            :alt="group.productName"
                        />
                        <div v-else class="icon-placeholder">
                            {{ group.productName ? group.productName.charAt(0) : '?' }}
                        </div>
                    </div>

                    <!-- 제품 카드 -->
                    <div class="product-card">
                        <!-- 제품 정보 -->
                        <div class="product-info">
                            <h3 class="product-name">{{ group.productName }}</h3>

                            <!-- OS 아이콘 (선택된 옵션 기준) -->
                            <div class="product-os">
                                <span 
                                    v-if="selectedOs[group.productName]" 
                                    class="os-badge"
                                >
                                    {{ selectedOs[group.productName] }}
                                </span>
                            </div>
                        </div>

                        <!-- 선택 옵션 (버전 / OS / 언어) -->
                        <div class="product-options">
                            <!-- 버전 -->
                            <select 
                                v-model="selectedVersions[group.productName]" 
                                class="version-select"
                                @change="onVersionChange(group)"
                            >
                                <option 
                                    v-for="version in getVersionOptions(group)" 
                                    :key="version || 'empty-version'"
                                    :value="version"
                                >
                                    {{ version || '버전 선택' }}
                                </option>
                            </select>

                            <!-- OS -->
                            <select 
                                v-model="selectedOs[group.productName]" 
                                class="os-select"
                                @change="onOsChange(group)"
                            >
                                <option 
                                    v-for="os in getOsOptions(group, selectedVersions[group.productName])" 
                                    :key="os || 'empty-os'"
                                    :value="os"
                                >
                                    {{ os || 'OS 선택' }}
                                </option>
                            </select>

                            <!-- 언어 -->
                            <select 
                                v-model="selectedLanguages[group.productName]" 
                                class="lang-select"
                            >
                                <option 
                                    v-for="lang in getLanguageOptions(group, selectedVersions[group.productName], selectedOs[group.productName])" 
                                    :key="lang || 'empty-lang'"
                                    :value="lang"
                                >
                                    {{ lang || '언어 선택' }}
                                </option>
                            </select>
                        </div>

                        <!-- 액션 버튼 -->
                        <div class="product-actions">
                            <button 
                                @click="downloadProduct(group)" 
                                class="btn-install"
                            >
                                설치
                            </button>
                            <button
                                v-if="hasBoardDetail(group)"
                                type="button"
                                @click="goToBoardDetail(group)"
                                class="btn-install"
                            >
                                자세히 보기
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'productInstall',
    data() {
        return {
            loading: false,
            productList: [],
            groupedProducts: [],
            searchText: '',
            selectedVersions: {},
            selectedOs: {},
            selectedLanguages: {},
            boardMap: {}, // gid를 키로 하는 게시글 정보 맵 { gid: { idx, gid } }
            thumbUrlMap: {}, // gid를 키로 하는 thumbUrl 맵 { gid: thumbUrl }
        }
    },
    created() {
        this.getProductList();
    },
    methods: {
        /**
         * 제품 목록 조회
         */
        getProductList() {
            this.loading = true;
            const params = {};
            if (this.searchText) {
                params.searchText = this.searchText;
            }

            this.axiosCaller.get(this, this.APIs.PRODUCT + '/getProductDownloadList', params, (res) => {
                try {
                    const data = res?.data || {};
                    if (data.status === 'ok') {
                        this.productList = data.list || [];
                        
                        // 게시글 정보를 gid 기준으로 맵 생성 (gid를 문자열로 변환하여 키로 사용)
                        this.boardMap = {};
                        (data.boardList || []).forEach(board => {
                            if (board.gid != null) {
                                // gid를 숫자로 변환 후 문자열로 변환하여 일관성 유지
                                const gidValue = Number(board.gid);
                                const gidKey = String(gidValue);
                                this.boardMap[gidKey] = {
                                    idx: board.idx,
                                    gid: gidValue
                                };
                            }
                        });
                        
                        // thumbUrlList를 gid를 키로 하는 맵으로 변환
                        this.thumbUrlMap = {};
                        if (data.thumbUrlList) {
                            Object.keys(data.thumbUrlList).forEach(gid => {
                                this.thumbUrlMap[String(gid)] = data.thumbUrlList[gid];
                            });
                        }
                        
                        // productName 기준으로 그룹핑
                        this.groupedProducts = this.buildGroupedProducts(this.productList);
                        // 그룹별 초기 선택값 설정
                        this.initSelections();
                    } else {
                        this.swalUtils.gritter('오류', data.msg || '제품 목록을 불러올 수 없습니다.', 'error');
                    }
                } catch (e) {
                    console.error('제품 목록 조회 실패:', e);
                    this.swalUtils.gritter('오류', '제품 목록을 불러올 수 없습니다.', 'error');
                } finally {
                    this.loading = false;
                }
            }, (err) => {
                console.error('제품 목록 조회 실패:', err);
                this.swalUtils.gritter('오류', '제품 목록을 불러올 수 없습니다.', 'error');
                this.loading = false;
            });
        },

        /**
         * 검색
         */
        doSearch() {
            this.getProductList();
        },

        /**
         * productName 기준 그룹핑
         */
        buildGroupedProducts(list) {
            const map = {};
            (list || []).forEach(product => {
                const key = product.productName || product.gid || '기타';
                if (!map[key]) {
                    const gidValue = product.gid != null ? Number(product.gid) : null;
                    const gidKey = gidValue != null ? String(gidValue) : null;
                    const boardInfo = gidKey ? this.boardMap[gidKey] : null;
                    
                    // representative에 iconUrl 추가 (thumbUrlMap에서 가져오기)
                    const representative = { ...product };
                    if (gidKey && this.thumbUrlMap[gidKey]) {
                        representative.iconUrl = this.thumbUrlMap[gidKey];
                    }
                    
                    map[key] = {
                        productName: key,
                        representative: representative,
                        gid: product.gid, // 그룹의 gid 저장 (대표 제품의 gid 사용)
                        boardIdx: boardInfo ? boardInfo.idx : null, // 게시글 idx 저장
                        variants: [],
                    };
                }
                // 원본 product 객체를 그대로 저장 (gid 포함)
                map[key].variants.push(product);
            });
            return Object.values(map);
        },

        /**
         * 그룹별 초기 선택값 설정 (가장 최신 버전 기준, exe 파일만)
         */
        initSelections() {
            this.selectedVersions = {};
            this.selectedOs = {};
            this.selectedLanguages = {};

            (this.groupedProducts || []).forEach(group => {
                if (!group.variants || group.variants.length === 0) {
                    return;
                }
                
                // exe 파일만 필터링
                const exeVariants = group.variants.filter(v => v.fileExtension === 'exe');
                if (exeVariants.length === 0) {
                    return;
                }
                
                // 버전별로 정렬하여 가장 최신 버전 찾기
                const sortedVariants = [...exeVariants].sort((a, b) => {
                    const versionA = parseInt(a.version) || 0;
                    const versionB = parseInt(b.version) || 0;
                    return versionB - versionA; // 내림차순 (최신 버전이 위로)
                });
                
                // 가장 최신 버전의 첫 번째 variant 사용 (set 반응형 동작을 위해 this.$set 사용)
                const latest = sortedVariants[0];
                this.$set(this.selectedVersions, group.productName, latest.version || '');
                this.$set(this.selectedOs, group.productName, latest.osType || '');
                this.$set(this.selectedLanguages, group.productName, latest.language || '');
            });
        },

        /**
         * 드롭다운 옵션 계산 helpers (exe 파일만)
         */
        getVersionOptions(group) {
            const set = new Set();
            (group.variants || []).forEach(v => {
                // exe 파일만 필터링
                if (v.fileExtension === 'exe' && v.version) {
                    set.add(v.version);
                }
            });
            // 버전을 내림차순으로 정렬 (최신 버전이 위로)
            return Array.from(set).sort((a, b) => {
                // 숫자로 변환하여 비교 (2025, 2024 등)
                const numA = parseInt(a) || 0;
                const numB = parseInt(b) || 0;
                return numB - numA; // 내림차순
            });
        },

        getOsOptions(group, version) {
            const set = new Set();
            (group.variants || []).forEach(v => {
                // exe 파일만 필터링
                if (v.fileExtension !== 'exe') return;
                if (version && v.version !== version) return;
                if (v.osType) {
                    set.add(v.osType);
                }
            });
            return Array.from(set);
        },

        getLanguageOptions(group, version, os) {
            const set = new Set();
            (group.variants || []).forEach(v => {
                // exe 파일만 필터링
                if (v.fileExtension !== 'exe') return;
                if (version && v.version !== version) return;
                if (os && v.osType !== os) return;
                if (v.language) {
                    set.add(v.language);
                }
            });
            return Array.from(set);
        },

        onVersionChange(group) {
            const key = group.productName;
            const version = this.selectedVersions[key];
            const osList = this.getOsOptions(group, version);
            this.$set(this.selectedOs, key, osList[0] || '');
            const langList = this.getLanguageOptions(group, version, this.selectedOs[key]);
            this.$set(this.selectedLanguages, key, langList[0] || '');
        },

        onOsChange(group) {
            const key = group.productName;
            const version = this.selectedVersions[key];
            const os = this.selectedOs[key];
            const langList = this.getLanguageOptions(group, version, os);
            this.$set(this.selectedLanguages, key, langList[0] || '');
        },

        /**
         * 제품 다운로드 (exe 파일만)
         */
        downloadProduct(group) {
            const key = group.productName;
            const version = this.selectedVersions[key];
            const os = this.selectedOs[key];
            const lang = this.selectedLanguages[key];

            // exe 파일만 필터링
            let matched = (group.variants || []).find(v =>
                v.fileExtension === 'exe' &&
                (!version || v.version === version) &&
                (!os || v.osType === os) &&
                (!lang || v.language === lang)
            );

            // exe 파일이 없으면 선택된 옵션에 맞는 exe 파일 찾기
            if (!matched) {
                matched = (group.variants || []).find(v =>
                    v.fileExtension === 'exe' &&
                    (!version || v.version === version) &&
                    (!os || v.osType === os)
                );
            }

            // 그래도 없으면 첫 번째 exe 파일 사용
            if (!matched) {
                matched = (group.variants || []).find(v => v.fileExtension === 'exe');
            }

            if (!matched || !matched.downloadLink) {
                this.swalUtils.gritter('알림', '설치 파일(exe)이 없습니다. 설치 안내사항을 확인해주세요.', 'info');
                return;
            }

            // 다운로드 링크로 이동
            const downloadUrl = this.buildProductDownloadUrl(matched.downloadLink, matched.fileName);
            window.open(downloadUrl, '_blank');
        },

        buildProductDownloadUrl(downloadLink, fileName) {
            if (!fileName || !downloadLink) {
                return downloadLink;
            }
            const separator = downloadLink.includes('?') ? '&' : '?';
            return downloadLink + separator + 'filename=' + encodeURIComponent(fileName);
        },

        /**
         * 게시판 상세로 이동 (자세한 설치방법)
         * 모든 제품에 자세히 보기 버튼 표시
         * gid가 있으면 해당 게시글로, 없으면 기본 게시글로 이동
         */
        hasBoardDetail(group) {
            return true;
        },

        goToBoardDetail(group) {
            const boardKey = 'product_download';
            let boardIdx = null;

            // gid가 있고 boardIdx가 있으면 해당 게시글로 이동
            if (group && group.boardIdx != null && group.boardIdx > 0) {
                boardIdx = group.boardIdx;
            } else if (group && group.gid != null) {
                // gid는 있지만 boardIdx가 없는 경우, gid를 사용하여 게시글 찾기
                const gidKey = String(group.gid);
                const boardInfo = this.boardMap[gidKey];
                if (boardInfo && boardInfo.idx) {
                    boardIdx = boardInfo.idx;
                }
            }

            // boardIdx가 있으면 해당 게시글로 이동
            if (boardIdx) {
                if (this.$router) {
                    this.$router.push({
                        name: 'productDownDetail',
                        params: {
                            boardKey: boardKey,
                            idx: boardIdx
                        }
                    });
                } else {
                    window.location.href = `/app/board/${boardKey}/detail/${boardIdx}`;
                }
            } else {
                // boardIdx가 없으면 제품명으로 게시글 검색하거나 기본 페이지로 이동
                // 또는 gid를 파라미터로 전달하여 상세 페이지에서 처리
                if (group && group.gid != null) {
                    if (this.$router) {
                        this.$router.push({
                            name: 'productDownDetail',
                            params: {
                                boardKey: boardKey,
                                idx: 0
                            },
                            query: {
                                gid: group.gid
                            }
                        });
                    } else {
                        window.location.href = `/app/board/${boardKey}/detail/0?gid=${group.gid}`;
                    }
                } else {
                    this.swalUtils.gritter('알림', '설치 안내사항이 준비되지 않았습니다.', 'info');
                }
            }
        }
    }
};
</script>

<style scoped>
.product-install-page {
    padding: 20px 0;
}

.page-title {
    margin-bottom: 40px;
    margin-top: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e9ecef;
}

.page-title .tit {
    font-size: 32px;
    font-weight: 700;
    color: #212529;
    margin: 0;
    line-height: 1.2;
}

/* 검색 바 */
.search-section {
    margin-bottom: 30px;
}

.search-bar {
    display: flex;
    gap: 10px;
    max-width: 600px;
}

.search-input {
    flex: 1;
    padding: 10px 15px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
}

.search-btn {
    padding: 10px 20px;
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
}

.search-btn:hover {
    background-color: #0056b3;
}

/* 제품 그리드 */
.product-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 30px;
    margin-top: 20px;
}

/* 제품 아이템 래퍼 */
.product-item-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 0;
}

/* 제품 아이콘 (카드 밖으로 분리) — height: 80% 는 그리드 행 높이와 맞물려 카드 영역을 압축할 수 있음 */
.product-icon {
    width: 100%;
    max-width: 300px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: transparent;
    border-radius: 12px;
    flex-shrink: 0;
    height: 180px;
}

.product-icon img {
    width: 300px;
    height: 180px;
    object-fit: cover;
    border-radius: 12px;
}

/* 제품 카드 */
.product-card {
    box-sizing: border-box;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 20px;
    width: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    overflow: hidden;
    transition: box-shadow 0.3s;
}

.product-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.icon-placeholder {
    font-size: 80px;
    font-weight: bold;
    color: #999;
}

/* 제품 정보 */
.product-info {
    flex: 1;
    margin-bottom: 15px;
}

.product-name {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 10px;
    color: #333;
}

.product-description {
    font-size: 14px;
    color: #666;
    line-height: 1.5;
    margin-bottom: 10px;
    min-height: 60px;
}

.product-os {
    margin-top: 10px;
}

.os-badge {
    display: inline-block;
    padding: 4px 8px;
    background-color: #e3f2fd;
    color: #1976d2;
    border-radius: 4px;
    font-size: 12px;
}

/* 선택 옵션 */
.product-options {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: left;
    gap: 8px;
    margin-bottom: 15px;
    flex-wrap: wrap;
}

.version-select,
.os-select,
.lang-select {
    padding: 4px 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 13px;
    background-color: white;
    min-width: 90px;
    height: 32px;
}

/* 액션 버튼 */
.product-actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: auto;
    flex-shrink: 0;
    width: 100%;
}

.btn-install {
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    padding: 12px;
    background-color: #000;
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.3s;
}

.btn-install:hover {
    background-color: #333;
}

/* 로딩 및 빈 상태 */
.loading,
.empty {
    text-align: center;
    padding: 40px;
    color: #999;
    font-size: 16px;
}

/* 반응형 */
@media (max-width: 768px) {
    .product-grid {
        grid-template-columns: 1fr;
    }
    
    .product-icon {
        width: 150px;
        height: 150px;
    }
    
    .icon-placeholder {
        font-size: 60px;
    }
}
</style>
